if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // ▼ ここがあなたの line item property 名（Liquid の name="properties[...]" と完全一致させる）
      get thanksPropertyName() {
        return 'Thanksカード用のお名前 or ニックネーム（ローマ字入力）';
      }

      // 現在のカートJSONを取得
      async fetchCartJson() {
        const res = await fetch(`${routes.cart_url}.js`, { credentials: 'same-origin' });
        if (!res.ok) throw new Error('Failed to fetch cart.js');
        return res.json();
      }

      // sections を取り直して cart-drawer/cart-notification を確実に最新描画する
      async fetchCartSections() {
        if (!this.cart) return null;

        const sectionIds = this.cart.getSectionsToRender().map((s) => s.id);
        // Shopify Sections Rendering API: /cart?sections=xxx,yyy
        const url = `${routes.cart_url}?sections=${encodeURIComponent(sectionIds.join(','))}`;

        const res = await fetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error('Failed to fetch cart sections');
        const sections = await res.json();

        // cart.renderContents は response.sections を参照するので同じ形にする
        return { sections };
      }

      // 同一SKU+THANKS同一（＋selling_plan同一も見る）なら true
      isSameSkuSameThanks(cartData, formData) {
        const variantId = String(formData.get('id') || '');
        const sellingPlan = String(formData.get('selling_plan') || ''); // 無い場合は ''
        const thanks = String(formData.get(`properties[${this.thanksPropertyName}]`) || '').trim();

        // cart.js の items は line 単位（properties 違いでも別要素）なので、完全一致の行があれば「追加しない」
        return cartData.items.some((item) => {
          const itemVariantId = String(item.variant_id);
          if (itemVariantId !== variantId) return false;

          const itemThanks = String(item.properties?.[this.thanksPropertyName] || '').trim();
          if (itemThanks !== thanks) return false;

          const itemSellingPlan = item.selling_plan_allocation?.selling_plan?.id
            ? String(item.selling_plan_allocation.selling_plan.id)
            : '';
          if (itemSellingPlan !== sellingPlan) return false;

          return true;
        });
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];

          const formData = new FormData(this.form);

          // ▼ sections 指定（Dawn標準）
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;

          // ★ 追加前にカートを確認して「同一SKU+THANKS同一」なら /cart/add を呼ばない
          //    → これで quantity が増えず、/cart リロードしても金額が増えない
          const cartData = await this.fetchCartJson();
          const shouldSkipAdd = this.isSameSkuSameThanks(cartData, formData);

          if (shouldSkipAdd) {
            // 追加せずにUIだけ最新に更新（drawer/notificationの見え方も揃える）
            if (this.cart) {
              const sectionsResponse = await this.fetchCartSections();
              if (sectionsResponse) this.cart.renderContents(sectionsResponse);
            } else {
              // cart通知が無いページならカートへ
              window.location = window.routes.cart_url;
            }

            // publish（必要なら）: 既存仕様に合わせて cartUpdate を投げる
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: cartData,
            });

            this.error = false;
            return;
          }

          // ★ 「同一SKU+THANKS違い」などは通常通り add → 別行が増える
          const response = await fetch(`${routes.cart_add_url}`, config).then((r) => r.json());

          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.errors || response.description,
              message: response.message,
            });

            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }

            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          // ▼ ここが「追加直後に別行が表示されない」対策の肝：
          // add.js の response だけに頼らず、sections を取り直して render する
          // （テーマや他JSの影響で add.js の sections が古く見えるケースを潰す）
          const freshSectionsResponse = await this.fetchCartSections();
          if (freshSectionsResponse) {
            this.cart.renderContents(freshSectionsResponse);
          } else {
            this.cart.renderContents(response);
          }

          if (!this.error) {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });
          }

          this.error = false;

          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(async () => {
                  const s = await this.fetchCartSections();
                  if (s) this.cart.renderContents(s);
                });
              },
              { once: true }
            );
            quickAddModal.hide(true);
          }
        } catch (e) {
          console.error(e);
        } finally {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
